import { money } from "./policy";
import type { CostSource, InvoiceTaxKind, ResourceLine, TaxScheme } from "./types";

/**
 * Внутренняя себестоимость строки.
 *
 * Она вводится, а не вычисляется. Разбор архива показал, как это устроено на
 * самом деле: у строк наших специалистов в колонке себестоимости стоит ноль —
 * заполненный, осмысленный ноль, — а расход появляется там, где работу делает
 * не наш человек: фрилансер, которого перепродаём по часам, или подрядчик с
 * пакетом. Правило «40% от цены» из ТЗ практике не соответствует и убрано.
 *
 * Налог на выплату идёт сверху договорной суммы. Аня на демо: «Если я с
 * фрилансером договорилась 80 на руки, а он самозанятый, я 6% не прибавила» —
 * то есть 80 это то, что получит исполнитель, а агентству строка обходится
 * дороже.
 *
 * **НДС юрлица — исключение, и это ответ Ани от 26 августа.** Мы предполагали,
 * что входящий НДС агентство к вычету не принимает, и считали его такой же
 * затратой: «нет, НДС нужно учитывать к вычету. Такое допущение делать
 * нельзя». Значит НДС по счёту исполнителя в себестоимость не идёт — он
 * возвращается зачётом. В деньгах он существует, поэтому считается отдельно
 * (`vatIn`), а не выбрасывается.
 *
 * **ОФК — это платёж наличными**, ответ Braining от 31 августа. Название
 * непрозрачное намеренно, и в интерфейсе с выгрузкой оно таким и остаётся:
 * переименовывать чужой эвфемизм в файле, который уходит наружу, не наше дело.
 *
 * На арифметику это ложится ровно так, как уже было: ОФК прибавляется
 * отдельным слагаемым (`Себестоимость = Сумма счёта + Расчёты в ОФК`), налог
 * на него не начисляется и к вычету он не идёт. И то и другое теперь не
 * умолчание, а следствие: у нала нет ни налога на выплату, ни счёта-фактуры.
 * Опасение, что ОФК дублирует колонку налога, снято — это не второй сбор, а
 * вторая половина того же платежа, наличная.
 */
export function lineCost(
  line: ResourceLine,
  scheme?: Pick<TaxScheme, "vatDeductible">,
): { cost: number; vatIn: number; source: CostSource } {
  const hasInvoice = typeof line.invoiceCost === "number";
  const hasOfk = typeof line.ofkCost === "number";

  if (hasInvoice || hasOfk) {
    const invoice = hasInvoice ? line.invoiceCost! : 0;
    const tax = money(invoice * (line.invoiceTaxRate ?? 0));

    // Вычет возможен, когда налог — это НДС и мы сами его плательщик на общем
    // режиме. У ИП на УСН вычета нет, и там тот же счёт обходится дороже.
    const deductible = Boolean(scheme?.vatDeductible) && taxKind(line) === "vat";

    return {
      cost: money(invoice + (deductible ? 0 : tax) + (hasOfk ? line.ofkCost! : 0)),
      vatIn: deductible ? tax : 0,
      source: "exact",
    };
  }

  // Исполнитель внешний, а расход ещё не заведён: ноль здесь — не значение,
  // а дыра, и прибыль по такой строке предварительная.
  if (isExternal(line)) return { cost: 0, vatIn: 0, source: "missing" };

  return { cost: 0, vatIn: 0, source: "own" };
}

/**
 * Ставки, за которыми стоит НДС.
 *
 * 22% — общая, 5% и 7% — у спецрежимов, 10% — льготная. Особняком 6%: это
 * налог самозанятого, к вычету он не принимается никогда, и спутать его с
 * НДС — значит завысить рентабельность на ровном месте.
 */
const VAT_RATES = new Set([0.05, 0.07, 0.1, 0.2, 0.22]);

/** Природа налога: сказанная явно либо угаданная по ставке. */
export function taxKind(line: ResourceLine): InvoiceTaxKind {
  if (line.invoiceTaxKind) return line.invoiceTaxKind;
  const rate = line.invoiceTaxRate ?? 0;
  return VAT_RATES.has(Number(rate.toFixed(4))) ? "vat" : "payout";
}

/** Строку делает не наш человек — фрилансер или подрядчик. */
export function isExternal(line: ResourceLine): boolean {
  return Boolean(line.freelance || line.contractor);
}
